import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import {
  EstadoGestion,
  EventoBitacora,
  EventoCanal,
  EventoEscenario,
  EventoResultado,
  FichaCliente,
  Gestion,
  MotivoBaja,
  Semaforo,
  SemanaDeCliente,
  SeguimientoService,
} from 'src/app/shared/services/seguimiento.service';

export interface DatosGestionModal {
  clienteId: number;
  nombre: string;
  tier: string;
  semanaId: number | null;
  tasaPreferenteMaxPct: number;
}

/**
 * La ficha del cliente: qué se hizo, qué se va a hacer y qué le pasó.
 *
 * DOS COSAS QUE NO SE PUEDEN TECLEAR ACÁ, Y ES EL PUNTO DE TODO EL MÓDULO:
 *
 * - **La fecha del último contacto.** Se registra un contacto y la fecha sale
 *   de ahí. Un campo editable convierte el indicador de tiempo de reacción en
 *   una opinión, y sobre ese indicador se evalúa a una persona.
 * - **La tasa preferente por encima del techo.** El techo lo carga Dirección;
 *   mientras esté en cero no hay oferta autorizada que registrar.
 */
@Component({
  selector: 'app-seguimiento-gestion-modal',
  templateUrl: './gestion-modal.component.html',
  styleUrls: ['./gestion-modal.component.sass'],
})
export class GestionModalComponent implements OnInit {
  cargando = true;
  guardando = false;
  registrando = false;
  hubocambios = false;

  gestion: Gestion | null = null;
  semanas: SemanaDeCliente[] = [];
  eventos: EventoBitacora[] = [];

  // ---- formulario de gestión
  estadoGestion: EstadoGestion = 'SIN_DUENO';
  motivoCodigo: MotivoBaja | '' = '';
  motivoDetalle = '';
  proximaAccion = '';
  proximaAccionAt = '';
  tasaPreferentePct: number | null = null;
  tasaPreferenteHasta = '';

  // ---- formulario de contacto
  canal: EventoCanal = 'WHATSAPP';
  escenario: EventoEscenario | '' = '';
  resultado: EventoResultado | '' = '';
  detalle = '';

  miSub: string | null = null;
  miEmail: string | null = null;

  readonly estados: EstadoGestion[] = [
    'SIN_DUENO',
    'EN_CONTACTO',
    'EN_RECUPERACION',
    'RECUPERADO',
    'PERDIDO',
    'NO_CONTACTABLE',
  ];

  readonly motivos: MotivoBaja[] = [
    'TASA',
    'TIEMPO_RESPUESTA',
    'COMPETENCIA',
    'LIMITES',
    'DISPONIBILIDAD',
    'CERRO_NEGOCIO',
    'ESTACIONAL',
    'OTRO',
    'DESCONOCIDO',
  ];

  readonly canales: EventoCanal[] = ['WHATSAPP', 'LLAMADA', 'MAIL', 'PRESENCIAL'];

  /** Los cuatro casos del guion, con lo que hay que hacer en cada uno. */
  readonly guion: Array<{ codigo: EventoEscenario; titulo: string; que: string }> = [
    {
      codigo: 'TASA',
      titulo: '«¿Cuál es la tasa?»',
      que: 'Anclar a un monto concreto y cerrar ahí mismo.',
    },
    {
      codigo: 'ME_FUI',
      titulo: '«Me fui porque…»',
      que: 'Validar, no discutir. Anotar el motivo tal como lo dijo.',
    },
    {
      codigo: 'LO_PIENSO',
      titulo: '«Lo pienso»',
      que: 'Agendar la próxima acción con fecha antes de cortar.',
    },
    {
      codigo: 'NO_CONTESTA',
      titulo: 'No contesta',
      que: 'Un solo recordatorio a las 48 h. El sistema lo marca.',
    },
  ];

  readonly resultados: EventoResultado[] = [
    'CONTESTO',
    'NO_CONTESTO',
    'AGENDADO',
    'RECHAZO',
    'VOLVIO_A_OPERAR',
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public datos: DatosGestionModal,
    private ref: MatDialogRef<GestionModalComponent>,
    private api: SeguimientoService,
    private cognito: CognitoService,
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      const session = await this.cognito.getSession();
      const payload = (session?.idToken as any)?.payload ?? {};
      this.miSub = payload.sub ?? null;
      this.miEmail = payload.email ?? null;
    } catch {
      // Sin sesión no se llega hasta acá; si pasa, el botón de tomar la cuenta
      // simplemente no aparece.
    }
    await this.cargar();
  }

  private async cargar(): Promise<void> {
    this.cargando = true;
    try {
      const ficha: FichaCliente = await this.api.ficha(this.datos.clienteId);
      this.gestion = ficha.gestion;
      this.semanas = ficha.semanas ?? [];
      this.eventos = ficha.eventos ?? [];

      if (this.gestion) {
        this.estadoGestion = this.gestion.estadoGestion;
        this.motivoCodigo = this.gestion.motivoCodigo ?? '';
        this.motivoDetalle = this.gestion.motivoDetalle ?? '';
        this.proximaAccion = this.gestion.proximaAccion ?? '';
        this.proximaAccionAt = this.gestion.proximaAccionAt ?? '';
        this.tasaPreferentePct = this.gestion.tasaPreferentePct;
        this.tasaPreferenteHasta = this.gestion.tasaPreferenteHasta ?? '';
      }
    } catch (e: any) {
      Swal.fire({
        title: 'No se pudo cargar la ficha',
        text: e?.error?.message ?? '',
        icon: 'error',
        confirmButtonColor: '#2F4A2B',
      });
    } finally {
      this.cargando = false;
    }
  }

  get sinDueno(): boolean {
    return !this.gestion?.duenoSub;
  }

  get esMia(): boolean {
    return !!this.miSub && this.gestion?.duenoSub === this.miSub;
  }

  get puedeOfrecerTasa(): boolean {
    return this.datos.tasaPreferenteMaxPct > 0;
  }

  /** Toma la cuenta a su nombre: es lo que cuenta para la cobertura de alertas. */
  async tomarCuenta(): Promise<void> {
    if (!this.miSub) return;
    await this.guardar({ duenoSub: this.miSub, duenoEmail: this.miEmail });
  }

  async liberarCuenta(): Promise<void> {
    const ok = await Swal.fire({
      title: '¿Dejar la cuenta sin dueño?',
      text: 'Si es un whale en rojo, va a aparecer en el indicador de whales sin responsable.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Dejarla sin dueño',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#A3432B',
    });
    if (ok.isConfirmed) await this.guardar({ duenoSub: null, duenoEmail: null });
  }

  async guardarFormulario(): Promise<void> {
    await this.guardar({
      estadoGestion: this.estadoGestion,
      motivoCodigo: this.motivoCodigo || null,
      motivoDetalle: this.motivoDetalle || null,
      proximaAccion: this.proximaAccion || null,
      proximaAccionAt: this.proximaAccionAt || null,
      tasaPreferentePct: this.tasaPreferentePct,
      tasaPreferenteHasta: this.tasaPreferenteHasta || null,
    });
  }

  private async guardar(cuerpo: Record<string, unknown>): Promise<void> {
    if (this.guardando) return;
    this.guardando = true;
    try {
      this.gestion = await this.api.guardarGestion(this.datos.clienteId, cuerpo as any);
      this.estadoGestion = this.gestion.estadoGestion;
      this.hubocambios = true;
      await this.cargar();
    } catch (e: any) {
      Swal.fire({
        title: 'No se pudo guardar',
        text: e?.error?.message ?? 'Revisá los datos e intentá de nuevo.',
        icon: 'error',
        confirmButtonColor: '#2F4A2B',
      });
    } finally {
      this.guardando = false;
    }
  }

  /**
   * Registra el contacto. Esto —y no un campo de fecha— es lo que mueve el
   * «último contacto» y el reloj del tiempo de reacción.
   */
  async registrarContacto(): Promise<void> {
    if (this.registrando) return;
    if (!this.resultado) {
      Swal.fire({
        title: 'Falta el resultado',
        text: '¿Contestó, no contestó, quedó agendado? Es lo que después explica el número.',
        icon: 'info',
        confirmButtonColor: '#2F4A2B',
      });
      return;
    }
    this.registrando = true;
    try {
      await this.api.registrarEvento(this.datos.clienteId, {
        tipo: 'CONTACTO',
        canal: this.canal,
        escenario: this.escenario || null,
        resultado: this.resultado,
        detalle: this.detalle || null,
        seguimientoSemanaId: this.datos.semanaId,
        proximaAccion: this.proximaAccion || null,
        proximaAccionAt: this.proximaAccionAt || null,
      });
      this.detalle = '';
      this.escenario = '';
      this.resultado = '';
      this.hubocambios = true;
      await this.cargar();
    } catch (e: any) {
      Swal.fire({
        title: 'No se pudo registrar el contacto',
        text: e?.error?.message ?? '',
        icon: 'error',
        confirmButtonColor: '#2F4A2B',
      });
    } finally {
      this.registrando = false;
    }
  }

  async agregarNota(): Promise<void> {
    const r = await Swal.fire({
      title: 'Nota',
      input: 'textarea',
      inputPlaceholder: 'Lo que haga falta recordar de esta cuenta…',
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#2F4A2B',
    });
    if (!r.isConfirmed || !r.value) return;
    await this.api.registrarEvento(this.datos.clienteId, {
      tipo: 'NOTA',
      detalle: r.value,
      seguimientoSemanaId: this.datos.semanaId,
    });
    this.hubocambios = true;
    await this.cargar();
  }

  cerrar(): void {
    this.ref.close(this.hubocambios);
  }

  claseSemaforo(s: Semaforo): string {
    return `sem-${s.toLowerCase()}`;
  }

  porcentaje(r: number | null): string {
    return r === null ? '–' : `${Math.round(r * 100)}%`;
  }

  etiquetaEvento(e: EventoBitacora): string {
    const partes = [e.tipo.toLowerCase().replace('_', ' ')];
    if (e.canal && e.canal !== 'SISTEMA') partes.push(e.canal.toLowerCase());
    if (e.resultado) partes.push(e.resultado.toLowerCase().replace('_', ' '));
    return partes.join(' · ');
  }
}
